/**
 * ProcessGenerationPage — Generation shell (Block 2026-08-02).
 *
 * Spec ref: PRODUCT_SPEC_WORKFLOW_MATCHING_TRANSLATION_GENERATION_V1.md §16
 * (owner-approved page structure) + UX_PROCESS_SHELL_AND_RESULTS_V1.md §7.
 *
 * Composition:
 *   - shared ProcessControlBar (top)
 *   - full-width input column chip block (ColumnTagCloud)
 *   - split layout: left = content-type list (GenerationTypesPanel, ~1/3),
 *     right = selected type settings (GenerationSettingsPanel, ~2/3)
 *   - results grid (GenerationDataGrid) below, full width
 *   - prominent full-width "Start generation" at the bottom (spec §16.10),
 *     duplicating the top Start.
 *
 * Runtime wiring (variant A, agreed 2026-08-02): only `product_description`
 * is executed by BE today; other types' settings are captured in
 * `content_type_configs` and persisted into pipeline_config. `module_keys`
 * sent = runtime-enabled types the user enabled (guarded non-empty so the
 * request never 422s on `min_length=1`). `output_language: 'auto'` is mapped
 * to a concrete code in FE before sending (UX-J10 §8.2 — backend never sees
 * 'auto').
 */
import { useEffect, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useGenerationStore } from '@/store/generationStore';
import { extractApiError } from '@/lib/apiError';
import { useDatasetPreview } from '@/hooks/useFiles';
import {
  useStartPipeline,
  usePausePipeline,
  useResumePipeline,
  useCancelPipeline,
  usePipelineStatus,
} from '@/hooks/useGeneration';
import { ProcessControlBar, type ProcessExecutionState } from '@/components/process/ProcessControlBar';
import { ColumnTagCloud } from '@/components/generation/ColumnTagCloud';
import { GenerationDataGrid } from '@/components/generation/GenerationDataGrid';
import { GenerationTypesPanel } from '@/components/generation/GenerationTypesPanel';
import { GenerationSettingsPanel } from '@/components/generation/GenerationSettingsPanel';
import { RUNTIME_ENABLED_KEYS } from '@/components/generation/contentTypes';

/** UX-J10 §8.2: 'auto' must be resolved in FE — backend never sees 'auto'. */
function resolveOutputLanguage(lang: string, datasetLocale?: string): string {
  if (lang && lang !== 'auto') return lang;
  if (datasetLocale && /^[a-z]{2}$/i.test(datasetLocale)) return datasetLocale.toLowerCase();
  return 'ru'; // pragmatic default for the project's primary market
}

export function ProcessGenerationPage() {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const setDatasetId = useGenerationStore((s) => s.setDatasetId);
  const setTierKey = useGenerationStore((s) => s.setTierKey);
  const setOutputLanguage = useGenerationStore((s) => s.setOutputLanguage);
  const setIsTestRun = useGenerationStore((s) => s.setIsTestRun);
  const datasetId = useGenerationStore((s) => s.datasetId);
  const tierKey = useGenerationStore((s) => s.tierKey);
  const outputLanguage = useGenerationStore((s) => s.outputLanguage);
  const temperature = useGenerationStore((s) => s.temperature);
  const maxTokens = useGenerationStore((s) => s.maxTokens);
  const selectedColumns = useGenerationStore((s) => s.selectedColumns);
  const testRunLimit = useGenerationStore((s) => s.testRunLimit);
  const setTestRunLimit = useGenerationStore((s) => s.setTestRunLimit);
  const isTestRun = useGenerationStore((s) => s.isTestRun);
  const currentPipelineId = useGenerationStore((s) => s.currentPipelineId);
  const setCurrentPipelineId = useGenerationStore((s) => s.setCurrentPipelineId);
  const contentConfigs = useGenerationStore((s) => s.contentConfigs);

  const { data: preview } = useDatasetPreview(datasetId);
  const datasetLocale = (preview?.dataset?.raw_metadata as { language?: string } | undefined)?.language;

  // Initialize from URL params
  useEffect(() => {
    const dataset = searchParams.get('dataset');
    if (dataset) setDatasetId(dataset);
    const tier = searchParams.get('tier');
    if (tier === 'economy' || tier === 'optimal' || tier === 'max') setTierKey(tier);
    const lang = searchParams.get('lang');
    if (lang) setOutputLanguage(lang);
    const test = searchParams.get('test');
    if (test !== null) setIsTestRun(test === '1');
  }, [searchParams, setDatasetId, setTierKey, setOutputLanguage, setIsTestRun]);

  // === Execution wiring ===
  const start = useStartPipeline();
  const pause = usePausePipeline();
  const resume = useResumePipeline();
  const cancel = useCancelPipeline();
  const { data: pipeline } = usePipelineStatus(currentPipelineId);

  // Runtime module_keys = runtime-enabled types the user enabled. Guarded
  // non-empty: if none of the runtime types is enabled, fall back to
  // product_description (the foundation default) so the request never 422s
  // on `module_keys` min_length=1.
  const runtimeModuleKeys = useMemo(() => {
    const enabled = RUNTIME_ENABLED_KEYS.filter((k) => contentConfigs[k]?.enabled);
    return enabled.length > 0 ? enabled : ['product_description'];
  }, [contentConfigs]);

  const handleStart = () => {
    if (!datasetId) return;
    const body = {
      dataset_id: datasetId,
      tier_key: tierKey,
      module_keys: runtimeModuleKeys.length > 0 ? runtimeModuleKeys : ['product_description'],
      output_language: resolveOutputLanguage(outputLanguage, datasetLocale),
      temperature,
      max_tokens: maxTokens,
      selected_columns: selectedColumns.length > 0 ? selectedColumns : null,
      is_test_run: isTestRun,
      test_run_limit: Math.max(1, Math.min(20, testRunLimit)),
      // Per-content-type settings — really sent, persisted into pipeline_config.
      content_type_configs: contentConfigs as unknown as Record<string, Record<string, unknown>>,
    };
    start.mutate(body, { onSuccess: (pr) => setCurrentPipelineId(pr.id) });
  };

  const executionState: ProcessExecutionState = {
    status: pipeline?.status ?? 'idle',
    isStarting: start.isPending,
    canStart: !!datasetId && runtimeModuleKeys.length > 0 && !start.isPending,
    startLabel: isTestRun
      ? t('generation.execution.testRun', { defaultValue: 'Test run' })
      : t('generation.execution.fullRun', { defaultValue: 'Full run' }),
    onStart: handleStart,
    onPause: currentPipelineId ? () => pause.mutate(currentPipelineId) : undefined,
    onResume: currentPipelineId ? () => resume.mutate(currentPipelineId) : undefined,
    onCancel: currentPipelineId ? () => cancel.mutate(currentPipelineId) : undefined,
    pausePending: pause.isPending,
    resumePending: resume.isPending,
    cancelPending: cancel.isPending,
    progress:
      pipeline && pipeline.total_rows > 0
        ? { processed: pipeline.processed_rows, total: pipeline.total_rows }
        : undefined,
    // P0.1 2026-07-28: проброс ошибки старта (gate no_search_provider_key / 4xx).
    // P1-1 2026-08-03: читаем унифицированный backend-ответ {error:{message}} (main.py), не только legacy detail.
    errorMessage:
      extractApiError(start.error, undefined) ??
      pipeline?.error_message ??
      null,
  };

  const startLabel = isTestRun
    ? t('generation.execution.testRun', { defaultValue: 'Test run' })
    : t('generation.execution.fullRun', { defaultValue: 'Full run' });

  return (
    <div className="space-y-4">
      <div>
        {/* UX-J2: short H1 only, no marketing subtitle with provider brands. */}
        <h1 className="text-2xl font-semibold mb-1">
          {t('generation.title', { defaultValue: 'Content Generation' })}
        </h1>
      </div>

      <ProcessControlBar
        datasetId={datasetId}
        setDatasetId={setDatasetId}
        tierKey={tierKey}
        setTierKey={setTierKey}
        outputLanguage={outputLanguage}
        setOutputLanguage={setOutputLanguage}
        testRunLimit={testRunLimit}
        setTestRunLimit={setTestRunLimit}
        isTestRun={isTestRun}
        setIsTestRun={setIsTestRun}
        execution={executionState}
      />

      <ColumnTagCloud />

      {/* Split layout: left content-type list (~1/3) + right settings (~2/3).
          At <1024px collapses to a single column (spec §16.3 — do not force
          three columns on narrow viewports). */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-1">
          <GenerationTypesPanel />
        </div>
        <div className="lg:col-span-2">
          <GenerationSettingsPanel />
        </div>
      </div>

      <GenerationDataGrid />

      {/* Spec §16.10: prominent full-width bottom Start, duplicating top. */}
      <div className="card flex items-center justify-between gap-4">
        <div className="text-xs text-surface-500">
          {runtimeModuleKeys.length > 0
            ? t('generation.bottom.runtimeReady', {
                defaultValue: 'Runtime-ready types: {{count}}',
                count: runtimeModuleKeys.length,
              })
            : t('generation.bottom.noRuntime', {
                defaultValue: 'No runtime-ready type enabled — product_description will be used.',
              })}
        </div>
        <button
          onClick={handleStart}
          disabled={!datasetId || start.isPending}
          className="px-5 py-2.5 rounded-lg bg-primary-500 text-white text-sm font-semibold disabled:opacity-50 disabled:cursor-not-allowed hover:bg-primary-400 transition-colors"
        >
          {start.isPending
            ? t('generation.bottom.starting', { defaultValue: 'Starting…' })
            : t('generation.bottom.start', { defaultValue: 'Start generation' })}{' '}
          · {startLabel}
        </button>
      </div>
    </div>
  );
}